import {SearchNameType} from '@aboutyou/legacy-tracking-sdk';
import {useTrackSearch} from '@aboutyou/tracking';
import {useOnClickOutside, useOnTabOutside} from '@aboutyou/ui-components';
import {startTransition, useEffect, useState} from 'react';

export type onClickSuggestionType = ({
  searchTerm,
  searchType,
}: {
  searchTerm: string;
  searchType: SearchNameType;
}) => void;

const initialSearchState = {
  term: '',
};

export const useSearchBar = ({
  isSearchInTabletMode,
  isSearchBarExpanded,
  collapseSearchBar,
  expandSearchBar,
  searchInputRef,
  searchWrapperRef,
}: {
  isSearchInTabletMode: boolean;
  isSearchBarExpanded: boolean;
  collapseSearchBar?: VoidFunction;
  expandSearchBar?: VoidFunction;
  searchInputRef: React.RefObject<HTMLInputElement | null>;
  searchWrapperRef: React.RefObject<HTMLDivElement | null>;
}) => {
  const trackSearch = useTrackSearch();

  const [searchValue, setSearchValue] = useState(initialSearchState.term);
  const [isInputFocused, setIsInputFocused] = useState(false);

  const [suggestionSelected, setSuggestionSelected] = useState(false);

  const handleInputOnChange = (term: string) => {
    setSearchValue(term);
    initialSearchState.term = term;
  };

  useEffect(() => {
    if (isSearchBarExpanded) {
      searchInputRef?.current?.focus({
        preventScroll: true,
      });
    }
  }, [isSearchBarExpanded]);

  const onFocusSearchInput = () => {
    if (!isInputFocused) {
      trackSearch({
        searchAction: 'click',
        searchName: 'searchOpen',
      });
      setIsInputFocused(true);
    }
  };

  const collapseAndBlurInput = (shouldCollapse = true) => {
    if (shouldCollapse) {
      collapseSearchBar?.();
    }

    setSuggestionSelected(false);
    setIsInputFocused(false);
    searchInputRef?.current?.blur();
  };

  const trackSearchClose = () => {
    trackSearch({
      searchAction: 'click',
      searchName: 'searchClose',
    });
  };

  const closeSearchResult = () => {
    if (isInputFocused) {
      // This prevents accidental posting the searchClose-event
      // when the user has clicked on a search result. Input will then be
      // blurred and we have already posted an search event e.g. "suggestedCategory"
      trackSearchClose();
    }
    collapseAndBlurInput();
  };

  // Everything that calls this navigates away from the panel, so the bar has to
  // collapse with it: in tablet mode the expanded state - not the input focus -
  // is what keeps the bar wide, the page dimmed and the sticky search icon hidden.
  const handleRemoveInputFocus = () => {
    if (isInputFocused) {
      collapseAndBlurInput();
    }
  };

  const handleOnClickSearchIcon = () => {
    if (isSearchInTabletMode) {
      expandSearchBar?.();
    }
  };

  const handleSetSuggestionSelected = (suggestionsListIndex: number | null) => {
    startTransition(() => {
      setSuggestionSelected(
        Boolean(suggestionsListIndex || suggestionsListIndex === 0),
      );
    });
  };

  useOnClickOutside(searchWrapperRef, closeSearchResult);

  useOnTabOutside(searchWrapperRef, closeSearchResult, {
    trigger: 'onceAndReset',
  });

  return {
    isInputFocused,
    searchValue,
    trackSearchClose,
    closeSearchResult,
    onFocusSearchInput,
    handleRemoveInputFocus,
    handleOnClickSearchIcon,
    handleInputOnChange,
    collapseAndBlurInput,
    suggestionSelected,
    setSuggestionSelected: handleSetSuggestionSelected,
  };
};
